import {
  createContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { setToken } from "@/lib/token-manager";
import { reloadAtHome, resetUserCaches } from "@/lib/user-caches";
import {
  impersonateUser as impersonateUserApi,
  startKoryPreview,
  stopImpersonation as stopImpersonationApi,
  type ImpersonationResponse,
} from "@/services/admin-services";
import { useProfileData } from "@/hooks/use-profile-data";

export type ImpersonationState = {
  isImpersonating: boolean;
  /** A preview session: the caller sees the target's feed and cannot write. */
  isReadOnly: boolean;
  admin: { id: string; email: string } | null;
  targetUser: { id: string; email: string; displayName: string } | null;
  startImpersonation: (userId: string) => Promise<void>;
  /** Rejects with `status: 404` when the Kory preview feature is off. */
  startPreview: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
};

const ImpersonationContext = createContext<ImpersonationState | undefined>(
  undefined,
);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: profileData } = useProfileData();

  const [admin, setAdmin] = useState<{ id: string; email: string } | null>(
    null,
  );
  const [targetUser, setTargetUser] = useState<{
    id: string;
    email: string;
    displayName: string;
  } | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  // Hydrate impersonation state from /users/me response.
  // Only sync from server data when we don't already have local state
  // (i.e., on initial load / page refresh). Local state set by
  // startImpersonation/stopImpersonation takes precedence to avoid
  // flicker caused by resetUserCaches() temporarily clearing profileData.
  useEffect(() => {
    if (profileData?.impersonator) {
      setAdmin(profileData.impersonator);
      setIsReadOnly(profileData.impersonator.read_only === true);
      setTargetUser({
        id: profileData.id,
        email: profileData.email,
        displayName: profileData.display_name || profileData.email,
      });
    } else if (profileData && !profileData.impersonator) {
      // Only clear if we have a definitive server response saying no impersonation.
      // When profileData is undefined (cache cleared), keep existing local state.
      setAdmin(null);
      setIsReadOnly(false);
      setTargetUser(null);
    }
  }, [profileData?.impersonator, profileData?.id, profileData?.email, profileData?.display_name, profileData]);

  const isImpersonating = admin !== null;

  // A switch reloads the page; until it lands, the previous user's page is gone, so nothing clicked on
  // it runs with the new token.
  const [reloading, setReloading] = useState(false);

  const beginSession = useCallback(
    (data: ImpersonationResponse, readOnly: boolean) => {
      setToken(data.token);
      setAdmin(data.admin);
      setIsReadOnly(readOnly);
      setTargetUser({
        id: data.target_user.id,
        email: data.target_user.email,
        displayName: data.target_user.display_name,
      });
      // A switch of user: clear everything cached, then start the new user from a fresh page.
      resetUserCaches(queryClient, data.target_user.id);
      setReloading(true);
      reloadAtHome();
    },
    [queryClient],
  );

  const startImpersonation = useCallback(
    async (userId: string) => beginSession(await impersonateUserApi(userId), false),
    [beginSession],
  );

  const startPreview = useCallback(
    async (userId: string) => beginSession(await startKoryPreview(userId), true),
    [beginSession],
  );

  const stopImpersonation = useCallback(async () => {
    const data = await stopImpersonationApi();
    setToken(data.token);
    setAdmin(null);
    setIsReadOnly(false);
    setTargetUser(null);
    // A switch of user: clear everything cached, then start the new user from a fresh page.
    resetUserCaches(queryClient, data.user.id);
    setReloading(true);
    reloadAtHome();
  }, [queryClient]);

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating,
        isReadOnly,
        admin,
        targetUser,
        startImpersonation,
        startPreview,
        stopImpersonation,
      }}
    >
      {!reloading && children}
    </ImpersonationContext.Provider>
  );
}

export { ImpersonationContext };
